import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  Container,
  Paper,
  Button,
  IconButton,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
  MenuItem,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Snackbar,
  Link,
  InputAdornment,
  Divider,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import LoadingView from './LoadingView';
import TurquoiseTorihikisakiDialog from './TurquoiseTorihikisakiDialog';
import ClientContactsSection from './ClientContactsSection';
import CustomerContactList, { CustomerContact } from './CustomerContactList';
import CustomerTurquoiseCodeList, { TurquoiseCode } from './CustomerTurquoiseCodeList';
import CustomerAssetList, { CustomerAsset, previewKindOf } from './CustomerAssetList';
import CustomerAssetViewer from './CustomerAssetViewer';
import CustomerEmploymentHistoryList from './CustomerEmploymentHistoryList';
import CustomerMergeDialog from './CustomerMergeDialog';
import AddressAutocomplete, { BuildingLinkValue, BuildingSuggestion } from './AddressAutocomplete';
import * as Icons from '../constants/icons';
import { useMasterData } from '../providers/MasterDataProvider';
import { useAuth } from '../firebase/AuthProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { validateCrmCustomer, ValidationErrors, hasValidClientId, toKatakana } from '../utils/validation';
import { ep } from '../api/endpoints';

// 顧客フォームで選べる建物との関係区分 (other は建物詳細ページ側でのみ扱う)
const BUILDING_RELATION_OPTIONS: { code: string; label: string }[] = [
  { code: 'resident', label: '居住' },
  { code: 'tenant', label: '入居 (テナント)' },
  { code: 'owner', label: '所有者' },
];

interface CrmCustomerFormProps {
  customerId?: string | null;
  open?: boolean;          // モーダル表示時の開閉フラグ (isModal=true 時のみ有効)
  onClose: () => void;
  onSuccess: (customerId: string, displayName?: string) => void;
  // 保存処理が開始した瞬間（API リクエスト送信直前）に呼ばれる。親側でダイアログを即時非表示にしたいときに利用。
  onSaveStart?: () => void;
  // 保存処理が失敗したときに呼ばれる。onSaveStart でダイアログを閉じている親側はここで再表示するなど復帰処理に利用。
  onSaveError?: () => void;
  initialCustomerType?: 'individual' | 'corporate';
  // 新規登録時のフォーム初期値 (イベント参加者からの登録など、呼び出し元の情報を引き継ぐとき)。
  // employer_id と employer_name を渡すと勤務先の選択肢にも入る
  initialValues?: Record<string, any>;
  isModal?: boolean;       // true=Dialog、false=ページレイアウト
}

const UPSERT_URL = ep('upsert_crm_customer');
const DETAIL_URL = ep('get_crm_customer_detail');

// 建物リンクの関係区分 表示名
const RELATION_TYPE_LABELS: Record<string, string> = {
  owner: '所有者',
  resident: '居住',
  tenant: '入居',
  other: 'その他',
};
const SEARCH_CORP_URL = ep('search_crm_corporates');
const EMPLOYER_REF_URL = ep('count_crm_employer_references');
const CHECK_DUP_CORP_URL = ep('check_duplicate_corporates');
const CHECK_DUP_IND_URL = ep('check_duplicate_individuals');

// 詳細表示モード用：ラベル+値表示の共通コンポーネント
// URL 表示用: スキーム省略時は https を補って別タブで開く
const toHref = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const FieldDisplay: React.FC<{ label: string; value: any; size?: number; multiline?: boolean; type?: 'email' | 'url' }> = ({
  label,
  value,
  size = 6,
  multiline = false,
  type,
}) => (
  <Grid size={size}>
    <Box sx={{ pb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 0.25,
          minHeight: 20,
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          color: value ? 'text.primary' : 'text.disabled',
        }}
      >
        {type === 'email' && value ? (
          <Link
            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${value}`}
            target="_blank"
            rel="noopener"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {value}
          </Link>
        ) : type === 'url' && value ? (
          <Link
            href={toHref(String(value))}
            target="_blank"
            rel="noopener"
            sx={{ textDecoration: 'none', wordBreak: 'break-all', '&:hover': { textDecoration: 'underline' } }}
          >
            {value}
          </Link>
        ) : (
          value || '-'
        )}
      </Typography>
    </Box>
  </Grid>
);

// 編集モード用: 上段にラベル(グレー)、下段に入力欄
// 生年月日 (YYYY-MM-DD) を「YYYY/MM/DD (NN歳)」形式に整形する (未設定は空文字)
const formatBirthDate = (value?: string | null): string => {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(value);
  const [, y, mo, d] = m;
  const birth = new Date(Number(y), Number(mo) - 1, Number(d));
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const beforeBirthday =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 0 ? `${y}/${mo}/${d} (${age}歳)` : `${y}/${mo}/${d}`;
};

const LabeledField: React.FC<{
  label: string;
  required?: boolean;
  extra?: React.ReactNode;
  children: React.ReactNode;
  sx?: any;
}> = ({ label, required, extra, children, sx }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        variant="caption"
        sx={{ color: 'grey.600', fontSize: '0.75rem', lineHeight: 1.4 }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
        )}
      </Typography>
      {extra}
    </Box>
    {children}
  </Box>
);

// カード形式のセクションコンポーネント (親コンポーネント内で定義すると入力中に再マウントされ
// TextField のフォーカスが外れるため、モジュールスコープで定義する)
const FormSection: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <Box sx={{
    mb: 2,
    p: 2.5,
    bgcolor: 'white',
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid',
    borderColor: 'divider'
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pb: 1, color: 'text.secondary' }}>
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
    </Box>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const CrmCustomerForm: React.FC<CrmCustomerFormProps> = ({
  customerId,
  open = true,
  onClose,
  onSuccess,
  onSaveStart,
  onSaveError,
  initialCustomerType,
  initialValues,
  isModal = true,
}) => {
  const { staffList, branchList, loading: masterLoading } = useMasterData();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 表示モード: customerId がある時は最初に詳細表示モード(false)、新規時は編集モード(true)
  const [isEditing, setIsEditing] = useState(!customerId);
  const [customerType, setCustomerType] = useState<'individual' | 'corporate'>(initialCustomerType || 'individual');
  const [employerOptions, setEmployerOptions] = useState<any[]>([]);
  const [employerLoading, setEmployerLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  // 種別変換 (フロント上でフォームを切り替え、保存時にバックエンドへ反映)
  const [pendingTypeChange, setPendingTypeChange] = useState(false);
  const [originalSnapshot, setOriginalSnapshot] = useState<{
    customerType: 'individual' | 'corporate';
    formData: any;
    employerOptions: any[];
  } | null>(null);
  const [employerRefWarning, setEmployerRefWarning] = useState(0);
  // 顧客統合ダイアログ (重複顧客を残す側へ付け替えて消す側を削除)
  const [mergeOpen, setMergeOpen] = useState(false);
  // 完了通知スナックバー
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  // ターコイズ取引先 詳細 Dialog
  const [turquoiseDialogOpen, setTurquoiseDialogOpen] = useState(false);
  // 取引先マスタ詳細ダイアログで開くコード (複数コードのうちクリックされたもの)
  const [turquoiseDialogCd, setTurquoiseDialogCd] = useState<string | null>(null);
  const openTurquoiseDetail = (cd: string) => {
    setTurquoiseDialogCd(cd);
    setTurquoiseDialogOpen(true);
  };
  // ターコイズ詳細の右側に並べて表示する添付 (null = 横並びなし)
  const [compareAssetId, setCompareAssetId] = useState<number | null>(null);

  // 重複確認 Dialog (新規登録時の名寄せガード。法人・個人共通)
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const [dupCandidates, setDupCandidates] = useState<any[]>([]);
  const [checkingDup, setCheckingDup] = useState(false);

  // 住所の建物連動 (住所ファースト)。住所欄は通常入力で、保存時にシステムが
  // 住所から建物を検索・自動登録して紐付ける (auto 解決)。
  //  - buildingLink: 「建物から選択」補助導線での明示選択 (住所を手修正すると解除され auto へ戻る)
  //  - originalRelationId: 読込時に存在した連動リンクID (auto 解決の更新対象 / 住所クリア時の削除対象)
  //  - currentLinkLabel: 読込時の紐付け建物の表示用ラベル
  const [buildingLink, setBuildingLink] = useState<BuildingLinkValue | null>(null);
  const [originalRelationId, setOriginalRelationId] = useState<number | null>(null);
  const [currentLinkLabel, setCurrentLinkLabel] = useState<string>('');
  //  - roomNo: 号室・階の入力値 (関係リンク room_no と対応。住所2 は建物名専用にし号室は本欄で管理する)
  const [roomNo, setRoomNo] = useState<string>('');

  const [formData, setFormData] = useState<any>({
    email: '',
    phone: '',
    mobile_phone: '',
    // 複数連絡先 (personal / business × tel / mobile / fax / email)。代表行はサーバーで 6 列へ同期される
    contacts: [] as CustomerContact[],
    tags: [],
    // Individual
    last_name: '',
    first_name: '',
    last_name_kana: '',
    first_name_kana: '',
    birth_date: '',
    gender: '',
    occupation: '',
    employer_id: null,
    employer_name: '',
    // Corporate
    company_name: '',
    company_name_kana: '',
    representative_name: '',
    post_number: '',
    address_main: '',
    address_second: '',
    tel_number: '',
    fax_number: '',
    website_url: '',
    turquoise_codes: [] as TurquoiseCode[],
    assets: [] as CustomerAsset[],
    our_staff_id: '',
    our_staff_branch: '',
    customer_branch_name: '',
    remarks: '',
    // 事業者登録番号 (T+13桁、法人・個人事業主共通。mst_customers_base)
    invoice_reg_no: '',
  });

  useEffect(() => {
    if (open) {
      setErrors({});
      if (customerId) {
        setIsEditing(false); // 既存顧客は詳細表示モードで開く
        fetchDetail();
      } else {
        setIsEditing(true);  // 新規登録は編集モードで開く
        setBuildingLink(null);
        setOriginalRelationId(null);
        setCurrentLinkLabel('');
        setRoomNo('');
        if (initialValues) {
          setFormData((prev: any) => ({ ...prev, ...initialValues }));
          if (initialValues.employer_id && initialValues.employer_name) {
            setEmployerOptions([{ id: initialValues.employer_id, company_name: initialValues.employer_name }]);
          }
        }
      }
    }
    // initialValues は開いた時点の値だけを使う (親の再描画で入力中の内容を上書きしないため依存に入れない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, open]);

  // 新規登録時、当社主担当者をログインユーザーでデフォルト設定
  useEffect(() => {
    if (!open || customerId) return;
    if (!user?.email || staffList.length === 0) return;
    const operator = staffList.find((s: any) => s.staff_id === user.email);
    if (!operator) return;
    setFormData((prev: any) => {
      if (prev.our_staff_id) return prev;
      return {
        ...prev,
        our_staff_id: operator.staff_id,
        our_staff_branch: operator.branch || '',
      };
    });
  }, [open, customerId, user?.email, staffList]);

  // キャンセル時の挙動：
  //  - 種別変換中: スナップショットから元種別・元データへ復元
  //  - 既存編集中: 詳細を再取得して詳細表示モードへ
  //  - 新規登録中: モーダルを閉じる
  const handleCancel = () => {
    setErrors({});
    if (pendingTypeChange && originalSnapshot) {
      setCustomerType(originalSnapshot.customerType);
      setFormData(originalSnapshot.formData);
      setEmployerOptions(originalSnapshot.employerOptions);
      setOriginalSnapshot(null);
      setPendingTypeChange(false);
      setEmployerRefWarning(0);
      setIsEditing(false);
      return;
    }
    if (customerId) {
      fetchDetail(); // 編集内容を破棄するため再取得
      setIsEditing(false);
    } else {
      onClose();
    }
  };

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.post(DETAIL_URL, { customerId });
      if (res.data.status === 'success') {
        const detail = res.data.detail;
        setFormData(detail);
        setCustomerType(detail.customer_type);
        if (detail.employer_id && detail.employer_name) {
          setEmployerOptions([{ id: detail.employer_id, company_name: detail.employer_name }]);
        }
        // 住所連動リンクの初期化。有効な居住 → 入居 → 所有 の優先順で 1 件採用する。
        // 住所ファースト方式のため明示選択 (buildingLink) にはせず、
        // auto 解決の更新対象 (relation_id) と表示ラベルだけを保持する
        const rels: any[] = detail.building_relations || [];
        const primary = [
          rels.find((r: any) => r.is_primary && r.is_current),
          ...['resident', 'tenant', 'owner']
            .map(t => rels.find((r: any) => r.is_current && r.relation_type === t)),
        ]
          .find(Boolean) || null;
        setBuildingLink(null);
        setOriginalRelationId(primary ? primary.id : null);
        setRoomNo(primary?.room_no || '');
        setCurrentLinkLabel(
          primary ? `${primary.building_name || ''}${primary.room_no ? ' ' + primary.room_no : ''}`.trim() : ''
        );
      }
    } catch (err) {
      console.error('Error fetching customer detail:', err);
    } finally {
      setLoading(false);
    }
  };

  // 勤務先法人検索の debounce タイマーと最新リクエスト連番。
  // 連番でレスポンスの新旧を判定し、古いレスポンスが新しい結果を上書きするのを防ぐ。
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  const handleSearchCorporates = (value: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!value) {
      setEmployerOptions([]);
      setEmployerLoading(false);
      return;
    }
    setEmployerLoading(true);
    const seq = ++searchSeqRef.current;
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(SEARCH_CORP_URL, { query: value });
        if (seq !== searchSeqRef.current) return; // 古いレスポンスは破棄
        setEmployerOptions(res.data.status === 'success' ? (res.data.results || []) : []);
      } catch (err) {
        if (seq !== searchSeqRef.current) return;
        console.error('[Employer Search] Error:', err);
        setEmployerOptions([]);
      } finally {
        if (seq === searchSeqRef.current) setEmployerLoading(false);
      }
    }, 300);
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // フリガナ欄の確定時 (blur) にひらがな→カタカナ・半角カナ→全角カナへ自動変換する
  const handleKanaBlur = (field: string) => {
    const value = formData[field];
    if (!value) return;
    const converted = toKatakana(value);
    if (converted !== value) handleChange(field, converted);
  };

  // 建物選択の変更を受けて住所欄へ自動転記する。
  // 郵便番号・住所1は建物から、住所2は建物名のみ (号室は roomNo 欄で独立管理)。
  const handleBuildingLinkChange = (link: BuildingLinkValue | null) => {
    setBuildingLink(link);
    if (link) {
      setFormData((prev: any) => ({
        ...prev,
        post_number: link.postal_code || '',
        address_main: link.building_address || '',
        address_second: link.building_name || '',
      }));
    }
  };

  // 住所欄を手修正したら候補の明示選択を解除し、保存時の自動解決へ戻す
  const handleAddressFieldChange = (field: string, value: any) => {
    if (buildingLink) setBuildingLink(null);
    handleChange(field, value);
  };

  // 住所1の Autocomplete で既存建物を選択 → 住所転記 + 建物リンク確定
  const handleBuildingSuggestionSelect = (b: BuildingSuggestion) => {
    handleBuildingLinkChange({
      relation_id: originalRelationId,
      building_id: b.id,
      building_name: b.building_name || '',
      building_address: b.address_main || '',
      postal_code: b.postal_code || '',
      room_no: roomNo,
      relation_type: buildingLink?.relation_type || (customerType === 'corporate' ? 'tenant' : 'resident'),
    });
  };

  // 担当者選択時に支店コードもスナップショット保存
  const handleStaffChange = (staffId: string) => {
    const staff = staffList.find((s: any) => s.staff_id === staffId);
    setFormData((prev: any) => ({
      ...prev,
      our_staff_id: staffId,
      our_staff_branch: staff?.branch || '',
    }));
  };

  const handleSave = async () => {
    const ve = validateCrmCustomer(formData, customerType);
    setErrors(ve);
    if (Object.keys(ve).length > 0) {
      setSnackbar({ open: true, message: '入力内容を確認してください', severity: 'error' });
      return;
    }
    // 新規登録時は重複チェック(名寄せガード)。候補があれば確認ダイアログを出して保存を保留する。
    if (isNewRegistration) {
      setCheckingDup(true);
      try {
        let found: any[] = [];
        if (customerType === 'corporate') {
          // 法人: 会社名(部分一致)・電話 (電話は複数連絡先の代表行から取り出す)
          const corpTel =
            ((formData.contacts || []) as CustomerContact[]).find(
              (c) => c.contact_kind === 'tel' && c.is_primary && c.value,
            )?.value || formData.tel_number;
          const res = await axios.post(CHECK_DUP_CORP_URL, {
            company_name: formData.company_name || undefined,
            company_name_kana: formData.company_name_kana || undefined,
            tel_number: corpTel || undefined,
          });
          found = res.data.status === 'success' ? (res.data.candidates || []) : [];
        } else {
          // 個人: 氏名(完全一致)・メール・電話・携帯 (複数連絡先の個人用代表行から取り出す)
          const primaryOf = (kind: string) =>
            ((formData.contacts || []) as CustomerContact[]).find(
              (c) => c.scope === 'personal' && c.contact_kind === kind && c.is_primary && c.value,
            )?.value || undefined;
          const res = await axios.post(CHECK_DUP_IND_URL, {
            last_name: formData.last_name || undefined,
            first_name: formData.first_name || undefined,
            email: primaryOf('email'),
            phone: primaryOf('tel'),
            mobile_phone: primaryOf('mobile'),
          });
          found = res.data.status === 'success' ? (res.data.candidates || []) : [];
        }
        if (found.length > 0) {
          setDupCandidates(found);
          setDupDialogOpen(true);
          return; // 保存は保留 (ダイアログでユーザーが判断)
        }
      } catch (err) {
        console.error('Error checking duplicates:', err);
        // 重複チェックに失敗しても登録は止めず、そのまま保存へ進む
      } finally {
        setCheckingDup(false);
      }
    }
    await performSave();
  };

  // 既存顧客(法人/個人)を採用 (重複作成を回避)。onSuccess の既存配線で親へ既存IDを返す。
  const handleUseExisting = (candidate: any) => {
    setDupDialogOpen(false);
    onSuccess(candidate.id, candidate.display_name || candidate.company_name || undefined);
  };

  // 重複候補を確認のうえ、それでも新規として登録する
  const handleRegisterAnyway = async () => {
    setDupDialogOpen(false);
    await performSave();
  };

  const performSave = async () => {
    setSaving(true);
    // 親側でダイアログを即時閉じたい場合に通知（API は引き続きバックグラウンドで継続）
    onSaveStart?.();
    // 変換時のメッセージ生成用 (state クリア前に保持)
    const wasConverting = pendingTypeChange;
    const fromType = originalSnapshot?.customerType;
    const toType = customerType;
    // 住所の建物連動リンクの同期指示 (住所ファースト)。
    //  - 「建物から選択」の明示選択あり → 従来どおりそのリンクを upsert
    //  - 住所1 あり → auto=true (サーバー側で住所から建物を検索・自動登録して紐付け)
    //  - 住所1 を空にした & 読込時リンクが有った → そのリンクを削除
    //  号室は常に号室欄 (roomNo) の値を明示して送る (空なら住所2 末尾の数字から従来どおり自動分離)
    const roomNoValue = roomNo.trim();
    let buildingLinkPayload: any;
    if (buildingLink) {
      buildingLinkPayload = {
        relation_id: buildingLink.relation_id ?? originalRelationId,
        building_id: buildingLink.building_id,
        room_no: roomNoValue,
        relation_type: buildingLink.relation_type,
      };
    } else if ((formData.address_main || '').trim()) {
      buildingLinkPayload = { auto: true, relation_id: originalRelationId, room_no: roomNoValue };
    } else if (originalRelationId) {
      buildingLinkPayload = { relation_id: originalRelationId, remove: true };
    }
    try {
      const res = await axios.post(UPSERT_URL, {
        customerId: customerId,
        data: {
          ...formData,
          customer_type: customerType,
          operator_id: user?.email,
        },
        ...(buildingLinkPayload !== undefined ? { building_link: buildingLinkPayload } : {}),
      });
      if (res.data.status === 'success') {
        const savedId: string = res.data.customerId || customerId || '';
        // 保存成功時に変換進行中フラグ・スナップショットを破棄
        setPendingTypeChange(false);
        setOriginalSnapshot(null);
        setEmployerRefWarning(0);
        const message = wasConverting
          ? `${fromType === 'individual' ? '個人' : '法人'} → ${toType === 'individual' ? '個人' : '法人'} へ変換しました`
          : (customerId ? '保存しました' : '正常に登録されました');
        setSnackbar({ open: true, message, severity: 'success' });
        // ページモード（詳細画面）かつ既存編集時は、サーバーから最新データを再取得して詳細表示に戻す
        // （updated_at / updated_operator_name 等のサーバー生成値を反映するため）
        if (!isModal && customerId) {
          await fetchDetail();
          setIsEditing(false);
        }
        // 親側で新規作成顧客を即時選択できるよう display_name を組み立てて渡す
        const savedDisplayName =
          customerType === 'individual'
            ? `${formData.last_name || ''} ${formData.first_name || ''}`.trim()
            : `${formData.company_name || ''}${formData.customer_branch_name ? ' ' + formData.customer_branch_name : ''}`.trim();
        // 少し待ってから親へ通知 (画面遷移/モーダル close で snackbar が消える前に表示するため)
        setTimeout(() => onSuccess(savedId, savedDisplayName || undefined), 1500);
      } else {
        setSnackbar({ open: true, message: res.data.message || '保存に失敗しました', severity: 'error' });
        onSaveError?.();
      }
    } catch (err: any) {
      console.error('Error saving customer:', err);
      // 業務エラー (ターコイズコードが他顧客に登録済み 等) はサーバーのメッセージをそのまま表示
      const serverMsg = err?.response?.data?.message;
      setSnackbar({ open: true, message: serverMsg ? `保存に失敗しました。${serverMsg}` : '保存に失敗しました', severity: 'error' });
      onSaveError?.();
    } finally {
      setSaving(false);
    }
  };

  // 種別変換: フロント上でフォーム種別を切替＋プリフィル。実際のDB反映は保存時。
  const handleStartConvert = () => {
    if (!customerId) return;
    const nextType: 'individual' | 'corporate' =
      customerType === 'individual' ? 'corporate' : 'individual';

    // 元データをスナップショット (キャンセル時に復元)
    setOriginalSnapshot({
      customerType,
      formData: { ...formData },
      employerOptions: [...employerOptions],
    });

    // プリフィル後の formData を生成
    const prefilled = { ...formData };
    if (nextType === 'corporate') {
      // 個人 → 法人
      const fullName = `${formData.last_name || ''} ${formData.first_name || ''}`.trim();
      const fullKana = `${formData.last_name_kana || ''} ${formData.first_name_kana || ''}`.trim();
      prefilled.company_name = fullName;
      prefilled.company_name_kana = fullKana;
      prefilled.representative_name = fullName;
      // 電話番号を法人カラムへ引き継ぎ (individual.phone → corporate.tel_number)
      prefilled.tel_number = formData.phone || '';
      // 個人固有項目はクリア
      prefilled.last_name = '';
      prefilled.first_name = '';
      prefilled.last_name_kana = '';
      prefilled.first_name_kana = '';
      prefilled.gender = '';
      prefilled.birth_date = '';
      prefilled.occupation = '';
      prefilled.employer_id = null;
      prefilled.employer_name = '';
      prefilled.phone = '';
      prefilled.mobile_phone = '';
      // 法人が持てるのは会社用の固定電話と FAX のみ。個人用のそれらは引き継ぎ、
      // 携帯・メールと勤務先用の行は破棄する (サーバー側の変換と同じ規則)
      prefilled.contacts = (formData.contacts || [])
        .filter((c: CustomerContact) =>
          c.scope === 'personal' && (c.contact_kind === 'tel' || c.contact_kind === 'fax'))
        .map((c: CustomerContact) => ({ ...c, id: undefined, scope: 'business' as const }));
    } else {
      // 法人 → 個人
      prefilled.last_name = formData.representative_name || formData.company_name || '';
      prefilled.first_name = '';
      prefilled.last_name_kana = '';
      prefilled.first_name_kana = '';
      // 電話番号を個人カラムへ引き継ぎ (corporate.tel_number → individual.phone)
      prefilled.phone = formData.tel_number || '';
      prefilled.mobile_phone = '';
      // 法人の電話・FAX は本人の連絡先として引き継ぐ (2 件目以降も残す)
      prefilled.contacts = (formData.contacts || [])
        .filter((c: CustomerContact) => c.contact_kind === 'tel' || c.contact_kind === 'fax')
        .map((c: CustomerContact) => ({ ...c, id: undefined, scope: 'personal' as const }));
      // 法人固有項目はクリア
      prefilled.company_name = '';
      prefilled.company_name_kana = '';
      prefilled.representative_name = '';
      prefilled.tel_number = '';
      prefilled.fax_number = '';
      prefilled.website_url = '';
      prefilled.industry = '';
      prefilled.customer_branch_name = '';
    }

    setCustomerType(nextType);
    setFormData(prefilled);
    setEmployerOptions([]);
    setPendingTypeChange(true);
    setIsEditing(true);
    setErrors({});

    // 法人 → 個人時のみ、勤務先として参照されている個人顧客件数を取得
    setEmployerRefWarning(0);
    if (nextType === 'individual') {
      axios.post(EMPLOYER_REF_URL, { customerId })
        .then((res) => {
          if (res.data.status === 'success') {
            setEmployerRefWarning(res.data.count || 0);
          }
        })
        .catch((err) => {
          console.error('Error fetching employer reference count:', err);
        });
    }
  };

  // 詳細表示モード用: 建物との関係一覧 (住所情報セクション内に表示、建物詳細へ遷移可)
  const renderBuildingRelationRows = () => {
    const rels: any[] = formData.building_relations || [];
    if (rels.length === 0) return null;
    return (
      <Grid size={12}>
        <Box sx={{ pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
            建物
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
            {rels.map((rel: any) => (
              <Box key={rel.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Icons.HomeWork sx={{ fontSize: 16, color: '#2e7d32', flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  noWrap
                  onClick={() => navigate(`/crm/customers/building/${rel.building_id}`)}
                  sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                >
                  {rel.building_name}
                </Typography>
                {rel.room_no && (
                  <Typography variant="caption" color="text.secondary">{rel.room_no}</Typography>
                )}
                <Chip
                  label={RELATION_TYPE_LABELS[rel.relation_type] || rel.relation_type}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e8f5e9', color: '#2e7d32' }}
                />
                {rel.ownership_share && (
                  <Typography variant="caption" color="text.disabled">持分 {rel.ownership_share}</Typography>
                )}
                {rel.is_current === false && (
                  <Chip label="過去" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#eceff1', color: 'text.disabled' }} />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>
    );
  };

  // 詳細表示モード（個人）
  const renderIndividualView = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, md: 5 }}>
        <FormSection title="基本情報" icon={<Icons.PersonOutline sx={{ fontSize: 18 }} />}>
          <Grid size={12}>
            <Box sx={{ pb: 0, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 1 }}>
                <Chip
                  label="個人"
                  size="small"
                  variant="outlined"
                  color="success"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                />
                {customerId && (
                  <Chip
                    label={`顧客ID: ${customerId}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: 'grey.200',
                      color: 'grey.600',
                      fontWeight: 100,
                      borderRadius: 1,
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                氏名
              </Typography>
              <Box sx={{ mt: 0.25 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {`${formData.last_name || ''} ${formData.first_name || ''}`.trim() || '-'}
                </Typography>
                {(formData.last_name_kana || formData.first_name_kana) && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: -0.25 }}>
                    {`${formData.last_name_kana || ''} ${formData.first_name_kana || ''}`.trim()}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          <FieldDisplay label="生年月日" value={formatBirthDate(formData.birth_date)} size={12} />
          <Grid size={12}>
            <Box sx={{ pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                主担当者
              </Typography>
              <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                {formData.our_staff_id && (
                  <Chip
                    label={staffList.find((s: any) => s.staff_id === formData.our_staff_id)?.branch_name || formData.our_staff_branch}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: 'grey.100',
                      color: 'grey.700',
                      fontWeight: 700,
                      borderRadius: 1
                    }}
                  />
                )}
                <Typography variant="body2" sx={{ color: formData.our_staff_name ? 'text.primary' : 'text.disabled' }}>
                  {formData.our_staff_name || '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box sx={{ bgcolor: 'grey.200', borderRadius: 2, p: 1.5, mt: 1 }}>
              <Grid container spacing={1}>
                <FieldDisplay label="登録者" value={formData.created_operator_name} size={6} />
                <FieldDisplay label="登録日時" value={formData.created_at ? String(formData.created_at).substring(0, 16) : ''} size={6} />
                <FieldDisplay label="登録経緯" value={formData.registration_source === 'building_party' ? '建物関係者から登録' : '手動登録'} size={6} />
                <FieldDisplay label="最終更新者" value={formData.updated_operator_name} size={6} />
                <FieldDisplay label="最終更新日時" value={formData.updated_at ? String(formData.updated_at).substring(0, 16) : ''} size={6} />
              </Grid>
            </Box>
          </Grid>
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 7 }}>
        <FormSection title="連絡先" icon={<Icons.EmailOutlined sx={{ fontSize: 18 }} />}>
          <Grid size={12}>
            <CustomerContactList scope="personal" contacts={formData.contacts || []} readOnly />
          </Grid>
        </FormSection>
        <FormSection title="住所情報" icon={<Icons.LocationOnOutlined sx={{ fontSize: 18 }} />}>
          <FieldDisplay label="郵便番号" value={formData.post_number} size={4} />
          <Grid size={8} />
          <FieldDisplay
            label="住所１（市区町村）"
            size={12}
            value={
              formData.address_main ? (
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [formData.post_number, formData.address_main, formData.address_second]
                      .filter(Boolean)
                      .join(' ')
                  )}`}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline', color: 'primary.main' },
                    '&:hover .map-hover-icon': { opacity: 1 },
                  }}
                >
                  {formData.address_main}
                  <Icons.LocationOnOutlined
                    className="map-hover-icon"
                    sx={{ fontSize: 16, opacity: 0, transition: 'opacity 0.15s' }}
                  />
                </Link>
              ) : ''
            }
          />
          <FieldDisplay label="住所２（建物名）" value={formData.address_second} size={8} />
          <FieldDisplay label="号室・階" value={roomNo} size={4} />
          {renderBuildingRelationRows()}
        </FormSection>
      </Grid>
    </Grid>
  );

  // 詳細表示モード（法人）
  const renderCorporateView = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, md: 5 }}>
        <FormSection title="会社・担当情報" icon={<Icons.Business sx={{ fontSize: 18 }} />}>
          <Grid size={12}>
            <Box sx={{ pb: 0.5, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 1 }}>
                <Chip
                  label="法人"
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                />
                {customerId && (
                  <Chip
                    label={`顧客ID: ${customerId}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: 'grey.200',
                      color: 'grey.600',
                      fontWeight: 100,
                      borderRadius: 1,
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                会社名
              </Typography>
              <Box sx={{ mt: 0.25 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {formData.company_name || '-'}
                </Typography>
                {formData.company_name_kana && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: -0.25 }}>
                    {formData.company_name_kana}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mt: 2 }}>
                <FieldDisplay label="拠点名（支店・営業所等）" value={formData.customer_branch_name} size={12} />
              </Box>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box sx={{ pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                主担当者
              </Typography>
              <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                {formData.our_staff_id && (
                  <Chip
                    label={staffList.find((s: any) => s.staff_id === formData.our_staff_id)?.branch_name || formData.our_staff_branch}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: 'grey.100',
                      color: 'grey.700',
                      fontWeight: 700,
                      borderRadius: 1
                    }}
                  />
                )}
                <Typography variant="body2" sx={{ color: formData.our_staff_name ? 'text.primary' : 'text.disabled' }}>
                  {formData.our_staff_name || '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box sx={{ bgcolor: 'grey.200', borderRadius: 2, p: 1.5, mt: 1 }}>
              <Grid container spacing={1}>
                <FieldDisplay label="登録者" value={formData.created_operator_name} size={6} />
                <FieldDisplay label="登録日時" value={formData.created_at ? String(formData.created_at).substring(0, 16) : ''} size={6} />
                <FieldDisplay label="登録経緯" value={formData.registration_source === 'building_party' ? '建物関係者から登録' : '手動登録'} size={6} />
                <FieldDisplay label="最終更新者" value={formData.updated_operator_name} size={6} />
                <FieldDisplay label="最終更新日時" value={formData.updated_at ? String(formData.updated_at).substring(0, 16) : ''} size={6} />
              </Grid>
            </Box>
          </Grid>
        </FormSection>
      </Grid>
      <Grid size={{ xs: 12, md: 7 }}>
        <FormSection title="連絡先" icon={<Icons.EmailOutlined sx={{ fontSize: 18 }} />}>
          <FieldDisplay label="代表メールアドレス" value={formData.email} size={12} type="email" />
          <Grid size={12}>
            <LabeledField label="電話・FAX">
              <CustomerContactList
                scope="business"
                kinds={['tel', 'fax']}
                contacts={formData.contacts || []}
                readOnly
              />
            </LabeledField>
          </Grid>
        </FormSection>
        <FormSection title="住所情報" icon={<Icons.LocationOnOutlined sx={{ fontSize: 18 }} />}>
          <FieldDisplay label="郵便番号" value={formData.post_number} size={4} />
          <Grid size={8} />
          <FieldDisplay
            label="住所１（市区町村）"
            size={12}
            value={
              formData.address_main ? (
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [formData.post_number, formData.address_main, formData.address_second]
                      .filter(Boolean)
                      .join(' ')
                  )}`}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline', color: 'primary.main' },
                    '&:hover .map-hover-icon': { opacity: 1 },
                  }}
                >
                  {formData.address_main}
                  <Icons.LocationOnOutlined
                    className="map-hover-icon"
                    sx={{ fontSize: 16, opacity: 0, transition: 'opacity 0.15s' }}
                  />
                </Link>
              ) : ''
            }
          />
          <FieldDisplay label="住所２（建物名）" value={formData.address_second} size={8} />
          <FieldDisplay label="号室・階" value={roomNo} size={4} />
          {renderBuildingRelationRows()}
        </FormSection>
      </Grid>
    </Grid>
  );

  // 顧客種別による色テーマ (個人=ブルー, 法人=パープル)
  const themeColor = customerType === 'individual' ? '#1976d2' : '#6366f1';
  const isNewRegistration = !customerId;

  // フィルド入力スタイルを子フィールドへ流し込む (cascade)
  const filledInputCascade = {
    '&& .MuiOutlinedInput-root': {
      backgroundColor: '#f5f7fa',
      borderRadius: 2,
    },
    '&& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
    '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.12)',
    },
    '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: themeColor,
      borderWidth: 1,
    },
  };

  // ターコイズ詳細と横並び表示できる添付 (ダイアログ内で表示できる PDF・画像のみ)
  const viewableAssets: CustomerAsset[] = (formData.assets || []).filter(
    (a: CustomerAsset) => previewKindOf(a) !== 'other',
  );


  // /////////////////////////////////////////////////////////////
  // ⬅️ メインリターン: コンテンツを isModal で Dialog or ページラップ
  // /////////////////////////////////////////////////////////////
  const formContent = (
    <>
      {/* ヘッダー (タイトル + サブキャプション + 閉じる×) */}
      <Box sx={{ p: 3, pb: 2, position: 'relative' }}>
        {isModal && (
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 12, right: 12, color: 'text.disabled' }}
            size="small"
          >
            <Icons.Close />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, color: blueGrey[900], display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box sx={{ color: themeColor, display: 'flex' }}>
            {loading ? (
              <Icons.Sync sx={{ fontSize: 28 }} />
            ) : customerType === 'individual' ? (
              <Icons.PersonOutline sx={{ fontSize: 28 }} />
            ) : (
              <Icons.Apartment sx={{ fontSize: 28 }} />
            )}
          </Box>
          {isNewRegistration
            ? '新規顧客登録'
            : isEditing
              ? '顧客情報の編集'
              : '顧客情報の詳細'}
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ ml: 5.5 }}>
          {isNewRegistration
            ? '新しい顧客情報をシステムに追加します'
            : isEditing
              ? '入力内容を更新して保存してください'
              : ''}
        </Typography>
      </Box>

      {/* 個人 / 法人 切替トグル (新規登録時のみ) */}
      {isNewRegistration && (
        <Box sx={{ px: 3, pb: 2 }}>
          <ToggleButtonGroup
            value={customerType}
            exclusive
            fullWidth
            onChange={(_, v) => v && setCustomerType(v)}
            sx={{
              bgcolor: '#f5f5f7',
              borderRadius: 2,
              p: 0.5,
              gap: 0.5,
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '6px !important',
                textTransform: 'none',
                fontWeight: 700,
                color: 'text.secondary',
                py: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
                '&.Mui-selected': {
                  bgcolor: 'white',
                  color: themeColor,
                  border: `1px solid ${themeColor}`,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  '&:hover': { bgcolor: 'white' },
                },
              },
            }}
          >
            <ToggleButton value="individual">個人顧客</ToggleButton>
            <ToggleButton value="corporate">法人顧客</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box sx={{ px: 3, pt: 0, pb: 2, overflowY: isModal ? 'auto' : 'visible', ...filledInputCascade }}>
        {(loading || saving) ? (
          <LoadingView message={saving ? '保存中...' : '読み込み中...'} height={isModal ? '50vh' : '60vh'} size={50} />
        ) : !isEditing && customerId ? (
          // ============================
          // 詳細表示モード（Typography）
          // ============================
          <Box sx={{ mt: 1 }}>
            {customerType === 'individual' ? renderIndividualView() : renderCorporateView()}

            {customerType === 'individual' && (
              <FormSection title="勤務先・担当情報" icon={<Icons.Business sx={{ fontSize: 18 }} />}>
                <FieldDisplay label="勤務先法人" value={formData.employer_name} size={12} />
                <FieldDisplay label="役職・役割" value={formData.occupation} size={12} />
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', mb: 0.5 }}>
                    勤務先連絡先
                  </Typography>
                  <CustomerContactList scope="business" contacts={formData.contacts || []} readOnly />
                </Grid>
                {/* 勤務先履歴 (子表が正本。勤務先法人の変更で自動的に旧行が閉じ新行が開く) */}
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', mb: 0.5 }}>
                    勤務先履歴
                  </Typography>
                  <CustomerEmploymentHistoryList
                    customerId={customerId}
                    history={formData.employment_history || []}
                    onChanged={fetchDetail}
                    onOpenEmployer={(employerId) => navigate(`/crm/customers/${employerId}`)}
                  />
                </Grid>
              </FormSection>
            )}

            {/* 先方担当者（この法人を勤務先とする個人顧客）。法人の既存顧客のみ表示。 */}
            {customerType === 'corporate' && customerId && (
              <Box sx={{ mb: 2 }}>
                <ClientContactsSection clientId={customerId} clientName={formData.company_name} />
              </Box>
            )}

            <FormSection title="その他" icon={<Icons.ListAlt sx={{ fontSize: 18 }} />}>
              <Grid size={6}>
                <Box sx={{ pb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                    取引先マスタ (ターコイズコード)
                  </Typography>
                  <CustomerTurquoiseCodeList
                    codes={formData.turquoise_codes || []}
                    readOnly
                    onOpenDetail={openTurquoiseDetail}
                  />
                </Box>
              </Grid>
              <FieldDisplay label="事業者登録番号" value={formData.invoice_reg_no} size={6} />
              {customerType === 'corporate' ? (
                <>
                  <FieldDisplay label="業界" value={formData.industry} size={6} />
                  <FieldDisplay label="URL" value={formData.website_url} size={6} type="url" />
                </>
              ) : (
                <Grid size={6} />
              )}
              <FieldDisplay label="備考" value={formData.remarks} size={12} multiline />
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                  添付ファイル
                </Typography>
                <CustomerAssetList
                  customerId={customerId}
                  assets={formData.assets || []}
                  onChanged={fetchDetail}
                  onCompareWithTurquoise={
                    formData.turquoise_torihikisaki_cd
                      ? (asset) => {
                          // 代表ターコイズコードの詳細 (左) とプレビュー中のファイル (右) を横並びで開く
                          setCompareAssetId(asset.id);
                          openTurquoiseDetail(String(formData.turquoise_torihikisaki_cd));
                        }
                      : undefined
                  }
                />
              </Grid>
            </FormSection>
          </Box>
        ) : (
          // ============================
          // 編集モード（フォーム入力）
          // ============================
          <Box sx={{ mt: 1 }}>
            {pendingTypeChange && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {customerType === 'corporate'
                  ? '個人 → 法人 へ変換します。保存ボタンで確定されます。'
                  : '法人 → 個人 へ変換します。保存ボタンで確定されます。'}
                {customerType === 'individual' && employerRefWarning > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    この法人を勤務先とする個人顧客が {employerRefWarning} 件あります。保存すると勤務先が空になります。
                  </Box>
                )}
              </Alert>
            )}

            {customerType === 'individual' ? (
              <>
                <FormSection title="基本情報" icon={<Icons.CheckCircleOutline sx={{ fontSize: 18 }} />}>
                  <Grid size={6}>
                    <LabeledField label="姓" required>
                      <TextField fullWidth size="small" value={formData.last_name || ''} onChange={(e) => handleChange('last_name', e.target.value)} placeholder="例: 佐藤" />
                    </LabeledField>
                  </Grid>
                  <Grid size={6}>
                    <LabeledField label="名" required>
                      <TextField fullWidth size="small" value={formData.first_name || ''} onChange={(e) => handleChange('first_name', e.target.value)} placeholder="例: 大作" />
                    </LabeledField>
                  </Grid>
                  <Grid size={6}>
                    <LabeledField label="セイ">
                      <TextField fullWidth size="small" value={formData.last_name_kana || ''} onChange={(e) => handleChange('last_name_kana', e.target.value)} onBlur={() => handleKanaBlur('last_name_kana')} error={!!errors.last_name_kana} helperText={errors.last_name_kana} placeholder="例: サトウ" />
                    </LabeledField>
                  </Grid>
                  <Grid size={6}>
                    <LabeledField label="メイ">
                      <TextField fullWidth size="small" value={formData.first_name_kana || ''} onChange={(e) => handleChange('first_name_kana', e.target.value)} onBlur={() => handleKanaBlur('first_name_kana')} error={!!errors.first_name_kana} helperText={errors.first_name_kana} placeholder="例: ダイサク" />
                    </LabeledField>
                  </Grid>
                  <Grid size={6}>
                    <LabeledField label="生年月日">
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={formData.birth_date || ''}
                        onChange={(e) => handleChange('birth_date', e.target.value)}
                      />
                    </LabeledField>
                  </Grid>
                  <Grid size={6} />
                  <Grid size={12}>
                    <LabeledField label="当社主担当者">
                      <Autocomplete
                        options={staffList}
                        size="small"
                        fullWidth
                        loading={masterLoading}
                        noOptionsText={masterLoading ? '読み込み中...' : '該当なし'}
                        getOptionLabel={(option: any) => option.staff_name || ''}
                        value={staffList.find((s: any) => s.staff_id === formData.our_staff_id) || null}
                        onChange={(_, newValue: any) => handleStaffChange(newValue ? newValue.staff_id : '')}
                        isOptionEqualToValue={(option: any, value: any) => option.staff_id === value.staff_id}
                        renderOption={(props, option: any) => (
                          <li {...props} key={option.staff_id}>
                            {option.staff_name} ({option.branch_name})
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="氏名で検索" />
                        )}
                      />
                    </LabeledField>
                  </Grid>
                </FormSection>

                <FormSection title="連絡先・住所" icon={<Icons.LocationOnOutlined sx={{ fontSize: 18 }} />}>
                  <Grid size={4}>
                    <LabeledField label="郵便番号">
                      <TextField fullWidth size="small" value={formData.post_number || ''} onChange={(e) => handleAddressFieldChange('post_number', e.target.value)} placeholder="例: 100-0001" />
                    </LabeledField>
                  </Grid>
                  <Grid size={8} />
                  <Grid size={12}>
                    <LabeledField label="住所１(市区町村・番地)">
                      <AddressAutocomplete
                        mode="link"
                        value={formData.address_main || ''}
                        onTextChange={(text) => handleAddressFieldChange('address_main', text)}
                        onBuildingSelect={handleBuildingSuggestionSelect}
                        placeholder="住所を入力すると登録済みの建物を候補表示します"
                      />
                    </LabeledField>
                  </Grid>
                  <Grid size={8}>
                    <LabeledField label="住所２(建物名)">
                      <TextField fullWidth size="small" value={formData.address_second || ''} onChange={(e) => handleAddressFieldChange('address_second', e.target.value)} placeholder="例: アサカハイツ" />
                    </LabeledField>
                  </Grid>
                  <Grid size={4}>
                    <LabeledField label="号室・階">
                      <TextField fullWidth size="small" value={roomNo} onChange={(e) => setRoomNo(e.target.value)} placeholder="例: 201 / 3F" />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Icons.HomeWork sx={{ fontSize: 14, color: '#2e7d32' }} />
                        {currentLinkLabel
                          ? `現在の紐付け「${currentLinkLabel}」— 保存時に住所１・住所２(建物名)・号室から再判定されます`
                          : '保存時に住所１・住所２(建物名)から建物へ自動で紐付けられます（未登録の建物は自動登録、号室は関係リンクに保存）'}
                      </Typography>
                      {buildingLink && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <TextField
                            select
                            size="small"
                            label="関係区分"
                            value={buildingLink.relation_type}
                            onChange={(e) => handleBuildingLinkChange({ ...buildingLink, relation_type: e.target.value })}
                            sx={{ width: 170 }}
                          >
                            {BUILDING_RELATION_OPTIONS.map(opt => (
                              <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                            ))}
                          </TextField>
                          <Chip
                            icon={<Icons.CheckCircleOutline sx={{ fontSize: 14 }} />}
                            label={`建物「${buildingLink.building_name}」を選択中`}
                            size="small"
                            sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#e8f5e9', color: '#2e7d32' }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="連絡先 (個人用)">
                      <CustomerContactList
                        scope="personal"
                        contacts={formData.contacts || []}
                        onChange={(next) => handleChange('contacts', next)}
                        error={errors.contacts}
                      />
                    </LabeledField>
                  </Grid>
                </FormSection>

                <FormSection title="勤務先・担当情報" icon={<Icons.Business sx={{ fontSize: 18 }} />}>
                  <Grid size={12}>
                    <LabeledField label="勤務先法人">
                      <Autocomplete
                        freeSolo
                        options={employerOptions}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.display_name || option.company_name || '';
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterOptions={(x) => x}
                        loading={employerLoading}
                        noOptionsText="社名・拠点名を入力して検索してください"
                        value={formData.employer_id ? { id: formData.employer_id, company_name: formData.employer_name, display_name: formData.employer_name } : null}
                        inputValue={formData.employer_name || ''}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            handleChange('employer_name', value);
                            handleChange('employer_id', null);
                            handleSearchCorporates(value);
                          } else if (reason === 'clear') {
                            handleChange('employer_name', '');
                            handleChange('employer_id', null);
                          }
                        }}
                        onChange={(_, newValue) => {
                          if (newValue && typeof newValue === 'object' && newValue.id) {
                            handleChange('employer_id', newValue.id);
                            handleChange('employer_name', newValue.display_name || newValue.company_name);
                          }
                        }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.display_name || option.company_name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="社名・拠点名で検索"
                            error={!!errors.employer_id}
                            helperText={errors.employer_id}
                          />
                        )}
                        fullWidth
                      />
                    </LabeledField>
                  </Grid>
                  <Grid size={6}>
                    <LabeledField label="役職・役割">
                      <TextField fullWidth size="small" value={formData.occupation || ''} onChange={(e) => handleChange('occupation', e.target.value)} placeholder="例: 代表取締役, 課長, 医師" />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="勤務先連絡先 (会社用)">
                      <CustomerContactList
                        scope="business"
                        contacts={formData.contacts || []}
                        onChange={(next) => handleChange('contacts', next)}
                      />
                    </LabeledField>
                  </Grid>
                </FormSection>

                <FormSection title="取引先マスタ" icon={<Icons.Handshake sx={{ fontSize: 18 }} />}>
                  <Grid size={12}>
                    <LabeledField label="取引先コード (ターコイズ・複数可)">
                      <CustomerTurquoiseCodeList
                        codes={formData.turquoise_codes || []}
                        onChange={(codes) => handleChange('turquoise_codes', codes)}
                        onOpenDetail={openTurquoiseDetail}
                      />
                    </LabeledField>
                  </Grid>
                  <Grid size={6}>
                    <LabeledField label="事業者登録番号">
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.invoice_reg_no ?? ''}
                        onChange={(e) => handleChange('invoice_reg_no', e.target.value)}
                        error={!!errors.invoice_reg_no}
                        helperText={errors.invoice_reg_no}
                        placeholder="例: T1234567890123"
                        inputProps={{ maxLength: 14 }}
                      />
                    </LabeledField>
                  </Grid>
                </FormSection>

                <FormSection title="備考" icon={<Icons.ListAlt sx={{ fontSize: 18 }} />}>
                  <Grid size={12}>
                    <LabeledField label="備考">
                      <TextField fullWidth size="small" value={formData.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)} multiline rows={3} />
                    </LabeledField>
                  </Grid>
                </FormSection>
              </>
            ) : (
              <>
                <FormSection title="基本情報" icon={<Icons.CheckCircleOutline sx={{ fontSize: 18 }} />}>
                  <Grid size={12}>
                    <LabeledField label="会社名" required>
                      <TextField fullWidth size="small" value={formData.company_name || ''} onChange={(e) => handleChange('company_name', e.target.value)} placeholder="例: 株式会社ホゼナル" />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="会社名(カナ)">
                      <TextField fullWidth size="small" value={formData.company_name_kana || ''} onChange={(e) => handleChange('company_name_kana', e.target.value)} onBlur={() => handleKanaBlur('company_name_kana')} error={!!errors.company_name_kana} helperText={errors.company_name_kana} placeholder="例: カ）ホゼナル" />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="拠点名(支店・営業所等)">
                      <TextField fullWidth size="small" value={formData.customer_branch_name || ''} onChange={(e) => handleChange('customer_branch_name', e.target.value)} placeholder="例: 大阪支店" />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="当社主担当者">
                      <Autocomplete
                        options={staffList}
                        size="small"
                        fullWidth
                        loading={masterLoading}
                        noOptionsText={masterLoading ? '読み込み中...' : '該当なし'}
                        getOptionLabel={(option: any) => option.staff_name || ''}
                        value={staffList.find((s: any) => s.staff_id === formData.our_staff_id) || null}
                        onChange={(_, newValue: any) => handleStaffChange(newValue ? newValue.staff_id : '')}
                        isOptionEqualToValue={(option: any, value: any) => option.staff_id === value.staff_id}
                        renderOption={(props, option: any) => (
                          <li {...props} key={option.staff_id}>
                            {option.staff_name} ({option.branch_name})
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="氏名で検索" />
                        )}
                      />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="業界">
                      <TextField fullWidth size="small" value={formData.industry || ''} onChange={(e) => handleChange('industry', e.target.value)} />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="URL">
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.website_url || ''}
                        onChange={(e) => handleChange('website_url', e.target.value)}
                        error={!!errors.website_url}
                        helperText={errors.website_url}
                        placeholder="例: https://www.example.co.jp"
                        inputProps={{ maxLength: 255 }}
                      />
                    </LabeledField>
                  </Grid>
                </FormSection>

                <FormSection title="連絡先・住所" icon={<Icons.LocationOnOutlined sx={{ fontSize: 18 }} />}>
                  <Grid size={4}>
                    <LabeledField label="郵便番号">
                      <TextField fullWidth size="small" value={formData.post_number || ''} onChange={(e) => handleAddressFieldChange('post_number', e.target.value)} placeholder="例: 100-0001" />
                    </LabeledField>
                  </Grid>
                  <Grid size={8} />
                  <Grid size={12}>
                    <LabeledField label="住所１(市区町村・番地)">
                      <AddressAutocomplete
                        mode="link"
                        value={formData.address_main || ''}
                        onTextChange={(text) => handleAddressFieldChange('address_main', text)}
                        onBuildingSelect={handleBuildingSuggestionSelect}
                        placeholder="住所を入力すると登録済みの建物を候補表示します"
                      />
                    </LabeledField>
                  </Grid>
                  <Grid size={8}>
                    <LabeledField label="住所２(建物名)">
                      <TextField fullWidth size="small" value={formData.address_second || ''} onChange={(e) => handleAddressFieldChange('address_second', e.target.value)} placeholder="例: アサカハイツ" />
                    </LabeledField>
                  </Grid>
                  <Grid size={4}>
                    <LabeledField label="号室・階">
                      <TextField fullWidth size="small" value={roomNo} onChange={(e) => setRoomNo(e.target.value)} placeholder="例: 201 / 3F" />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Icons.HomeWork sx={{ fontSize: 14, color: '#2e7d32' }} />
                        {currentLinkLabel
                          ? `現在の紐付け「${currentLinkLabel}」— 保存時に住所１・住所２(建物名)・号室から再判定されます`
                          : '保存時に住所１・住所２(建物名)から建物へ自動で紐付けられます（未登録の建物は自動登録、号室は関係リンクに保存）'}
                      </Typography>
                      {buildingLink && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <TextField
                            select
                            size="small"
                            label="関係区分"
                            value={buildingLink.relation_type}
                            onChange={(e) => handleBuildingLinkChange({ ...buildingLink, relation_type: e.target.value })}
                            sx={{ width: 170 }}
                          >
                            {BUILDING_RELATION_OPTIONS.map(opt => (
                              <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                            ))}
                          </TextField>
                          <Chip
                            icon={<Icons.CheckCircleOutline sx={{ fontSize: 14 }} />}
                            label={`建物「${buildingLink.building_name}」を選択中`}
                            size="small"
                            sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#e8f5e9', color: '#2e7d32' }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="代表メールアドレス">
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.email || ''}
                        onChange={(e) => handleChange('email', e.target.value)}
                        InputProps={{
                          endAdornment: formData.email && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${formData.email}`, '_blank')}
                                title="Gmailで送信"
                              >
                                <Icons.EmailOutlined fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </LabeledField>
                  </Grid>
                  <Grid size={12}>
                    <LabeledField label="電話・FAX">
                      <CustomerContactList
                        scope="business"
                        kinds={['tel', 'fax']}
                        contacts={formData.contacts || []}
                        onChange={(next) => handleChange('contacts', next)}
                        error={errors.contacts}
                      />
                    </LabeledField>
                  </Grid>
                </FormSection>

                <FormSection title="取引先マスタ" icon={<Icons.Handshake sx={{ fontSize: 18 }} />}>
                  <Grid size={12}>
                    <LabeledField label="取引先コード (ターコイズ・複数可)">
                      <CustomerTurquoiseCodeList
                        codes={formData.turquoise_codes || []}
                        onChange={(codes) => handleChange('turquoise_codes', codes)}
                        onOpenDetail={openTurquoiseDetail}
                      />
                    </LabeledField>
                  </Grid>
                  <Grid size={6}>
                    <LabeledField label="事業者登録番号">
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.invoice_reg_no ?? ''}
                        onChange={(e) => handleChange('invoice_reg_no', e.target.value)}
                        error={!!errors.invoice_reg_no}
                        helperText={errors.invoice_reg_no}
                        placeholder="例: T1234567890123"
                        inputProps={{ maxLength: 14 }}
                      />
                    </LabeledField>
                  </Grid>
                </FormSection>

                <FormSection title="備考" icon={<Icons.ListAlt sx={{ fontSize: 18 }} />}>
                  <Grid size={12}>
                    <LabeledField label="備考">
                      <TextField fullWidth size="small" value={formData.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)} multiline rows={3} />
                    </LabeledField>
                  </Grid>
                </FormSection>
              </>
            )}
          </Box>
        )}
      </Box>
      {/* フッターアクション (モーダル表示時、詳細表示モード) 閉じる・編集 */}
      {isModal && !isEditing && !isNewRegistration && customerId && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'white',
          }}
        >
          <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX}>
            閉じる
          </Button>
          <Button onClick={() => setIsEditing(true)} startIcon={<Icons.Edit />} sx={STANDARD_CTA_BUTTON_SX}>
            編集
          </Button>
        </Box>
      )}
      {/* フッターアクション (モーダル表示時、かつ編集モード時のみ表示) */}
      {isModal && isEditing && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'white',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCancel} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
              キャンセル
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || checkingDup || (!formData.last_name && !formData.company_name)}
              startIcon={<Icons.Save />}
              sx={STANDARD_CTA_BUTTON_SX}
            >
              {checkingDup ? '重複確認中...' : saving ? '保存中...' : '保存する'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ターコイズ取引先 詳細 Dialog */}
      <TurquoiseTorihikisakiDialog
        open={turquoiseDialogOpen}
        onClose={() => {
          setTurquoiseDialogOpen(false);
          setTurquoiseDialogCd(null);
          setCompareAssetId(null);
        }}
        torihikisakiCd={turquoiseDialogCd ?? formData.turquoise_torihikisaki_cd ?? null}
        headerAction={
          // 添付 (PDF・画像) がある顧客は、ターコイズ詳細側からも横並び表示を切り替えられる
          viewableAssets.length > 0 ? (
            <Button
              size="small"
              startIcon={<Icons.VerticalSplit sx={{ fontSize: 16 }} />}
              onClick={() => setCompareAssetId(compareAssetId === null ? viewableAssets[0].id : null)}
              sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
            >
              {compareAssetId === null ? '添付ファイルを並べる' : '並べて表示を閉じる'}
            </Button>
          ) : undefined
        }
        sidePanel={
          compareAssetId !== null && viewableAssets.some((a) => a.id === compareAssetId) ? (
            <CustomerAssetViewer
              assets={viewableAssets}
              assetId={compareAssetId}
              onSelect={setCompareAssetId}
              onClose={() => setCompareAssetId(null)}
            />
          ) : undefined
        }
      />

      {/* 重複確認 Dialog (名寄せガード。法人=会社名/電話, 個人=氏名/メール/電話) */}
      <Dialog open={dupDialogOpen} onClose={() => setDupDialogOpen(false)} fullWidth maxWidth="sm" disableScrollLock>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.5 }}>
          <Icons.WarningAmber sx={{ fontSize: 20, color: 'warning.main' }} />
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
            重複の可能性がある{customerType === 'corporate' ? '法人' : '顧客'}が見つかりました
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 3, py: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {customerType === 'corporate'
              ? `同名・同電話の法人が ${dupCandidates.length} 件あります。別拠点でなければ、新規作成せず既存の法人をご利用ください。`
              : `同姓同名・同連絡先の顧客が ${dupCandidates.length} 件あります。別人でなければ、新規作成せず既存の顧客をご利用ください。`}
          </Alert>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {dupCandidates.map((c, idx) => {
              const isCorp = customerType === 'corporate';
              const title = isCorp
                ? c.company_name
                : `${c.last_name || ''} ${c.first_name || ''}`.trim();
              const badge = isCorp ? c.customer_branch_name : c.employer_name;
              const sub = isCorp
                ? [c.company_name_kana, c.tel_number, c.address_main]
                : [
                    `${c.last_name_kana || ''} ${c.first_name_kana || ''}`.trim(),
                    c.email,
                    c.mobile_phone || c.phone,
                    c.occupation,
                  ];
              return (
                <Box
                  key={c.id || idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderTop: idx > 0 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  {/* 左: 顧客情報 (flex:1 + minWidth:0 で自列内に折り返し) */}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                        {title || '(名称未設定)'}
                      </Typography>
                      {badge && (
                        <Chip label={badge} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', wordBreak: 'break-word' }}
                    >
                      {sub.filter(Boolean).join(' / ')}
                    </Typography>
                  </Box>
                  {/* 右: ボタン (縮まない・折り返さない) */}
                  <Button
                    size="small"
                    onClick={() => handleUseExisting(c)}
                    sx={{ ...STANDARD_CTA_BUTTON_SX, flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {isCorp ? 'この会社を使う' : 'この顧客を使う'}
                  </Button>
                </Box>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {customerType === 'corporate'
              ? '拠点名が異なる場合は別拠点として「新規として登録」してください。'
              : '別人の場合は「新規として登録」してください。'}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, py: 1.5 }}>
          <Button onClick={() => setDupDialogOpen(false)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={handleRegisterAnyway} disabled={saving} sx={STANDARD_CTA_BUTTON_SX}>
            新規として登録
          </Button>
        </Box>
      </Dialog>

      {/* 完了通知スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 顧客統合ダイアログ (既存顧客のみ) */}
      {customerId && mergeOpen && (
        <CustomerMergeDialog
          open={mergeOpen}
          onClose={() => setMergeOpen(false)}
          currentCustomerId={customerId}
          currentCustomerType={customerType}
          currentDisplayName={
            customerType === 'individual'
              ? `${formData.last_name || ''} ${formData.first_name || ''}`.trim()
              : `${formData.company_name || ''}${formData.customer_branch_name ? ' ' + formData.customer_branch_name : ''}`.trim()
          }
          onMerged={(targetId) => {
            // 残した側が自分なら再取得、相手なら相手の詳細へ遷移 (自分は削除済み)
            if (targetId === customerId) {
              fetchDetail();
              onSuccess(targetId);
            } else if (!isModal) {
              navigate(`/crm/customers/${targetId}`);
            } else {
              onSuccess(targetId);
              onClose();
            }
          }}
        />
      )}
    </>
  );

  if (!isModal) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            startIcon={<Icons.ArrowBack />}
            onClick={onClose}
            sx={STANDARD_TEXT_BUTTON_SX}
          >
            顧客一覧へ戻る
          </Button>

          {/* アクションボタン */}
          {isEditing ? (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {!isNewRegistration && !pendingTypeChange && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<Icons.SwapHoriz />}
                  onClick={handleStartConvert}
                  sx={{ textTransform: 'none', borderRadius: 2, mr: 4 }}
                >
                  {customerType === 'individual' ? '法人へ変換' : '個人へ変換'}
                </Button>
              )}
              <Button
                onClick={handleCancel}
                disabled={saving}
                sx={STANDARD_TEXT_BUTTON_SX}
              >
                キャンセル
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={saving || checkingDup || (!formData.last_name && !formData.company_name)}
                startIcon={<Icons.Save />}
                sx={{ ...STANDARD_CTA_BUTTON_SX, px: 3 }}
              >
                {checkingDup ? '重複確認中...' : saving ? '保存中...' : '保存する'}
              </Button>
            </Box>
          ) : (
            !isNewRegistration && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icons.CallMerge />}
                  onClick={() => setMergeOpen(true)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  他の顧客と統合
                </Button>
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="contained"
                  size="small"
                  sx={{ ...STANDARD_CTA_BUTTON_SX, px: 3 }}
                >
                  編集
                </Button>
              </Box>
            )
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          {formContent}
        </Box>
      </Container>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableScrollLock
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {formContent}
    </Dialog>
  );
};

export default CrmCustomerForm;
